import { useEffect, useRef } from 'react';

interface UseHubspotCookieBannerFocusParams {
  emailInputId?: string;
  bannerId?: string;
  okButtonId?: string;
}

export const useHubspotCookieBannerFocus = ({
  emailInputId = 'emailInput',
  bannerId = 'hs-eu-cookie-confirmation',
  okButtonId = 'hs-eu-confirmation-button',
}: UseHubspotCookieBannerFocusParams = {}) => {
  const hasFocusedBannerOnLoadRef = useRef<boolean>(false);
  const wasBannerPresentRef = useRef<boolean>(false);

  useEffect(() => {
    const focusEmailInput = () => {
      const emailInputElement = document.getElementById(emailInputId) as HTMLInputElement | null;
      emailInputElement?.focus();
    };

    const isElementVisible = (element: HTMLElement): boolean => {
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (Number(style.opacity) === 0) return false;
      return element.getClientRects().length > 0;
    };

    const getHubspotCookieBanner = (): HTMLElement | null => {
      const banner = document.getElementById(bannerId) as HTMLElement | null;
      if (!banner) return null;
      return isElementVisible(banner) ? banner : null;
    };

    const prepareAndFocusBanner = (banner: HTMLElement) => {
      banner.setAttribute('tabindex', '-1');
      if (!hasFocusedBannerOnLoadRef.current) {
        hasFocusedBannerOnLoadRef.current = true;
        banner.focus({ preventScroll: true });
      }
    };

    const initialBanner = getHubspotCookieBanner();
    if (initialBanner) {
      wasBannerPresentRef.current = true;
      prepareAndFocusBanner(initialBanner);
    } else {
      focusEmailInput();
    }

    const mutationObserver = new MutationObserver(() => {
      const banner = getHubspotCookieBanner();
      if (banner) {
        wasBannerPresentRef.current = true;
        prepareAndFocusBanner(banner);
        return;
      }

      const bannerWasPresent = wasBannerPresentRef.current;
      if (bannerWasPresent && hasFocusedBannerOnLoadRef.current) {
        wasBannerPresentRef.current = false;
        window.requestAnimationFrame(() => focusEmailInput());
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden'],
    });

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const okButton = target.closest(`#${okButtonId}`) as HTMLElement | null;
      if (!okButton) return;

      window.setTimeout(() => focusEmailInput(), 0);
    };

    document.addEventListener('click', clickHandler, true);

    return () => {
      document.removeEventListener('click', clickHandler, true);
      mutationObserver.disconnect();
    };
  }, [bannerId, emailInputId, okButtonId]);
};

